<template>
  <section :data-active="dropZoneActive" @dragenter.prevent="setActive" @dragover.prevent="setActive"
           @drop.prevent="onDrop">
    <div v-if="dropZoneActive" class="drag-area" @dragleave.prevent="setInactive">
      <v-icon large>mdi-download</v-icon>
      <h3>{{ $t('dragAttachmentHere') }}</h3>
    </div>
    <v-card :class="dropZoneActive ? 'drop-zone-enable' : ''" color="#FBFBFB" elevation="1" class="test-markdown">
      <v-card-title style="padding-bottom: 0px">
        <markdown-toolbar text-area-id="text-area" class="pb-2" v-if="advanceMode"/>
        <MenuBar :editor="editor" class="pb-2" v-if="!advanceMode && editor"/>
      </v-card-title>
      <v-card-text>
        <tecna-markdown v-model="model" :editor="editor" :configuration="configuration" v-if="advanceMode"/>
        <text-editor :editor="editor" v-if="!advanceMode"/>
      </v-card-text>
      <v-card-actions style="margin-left: 8px; margin-right: 8px">
        <v-row>
          <v-col md="9"
                 sm="9"
                 xs="12" class="d-flex"
                 style="margin-top: 0px; padding-top: 0px!important;">
             <span v-if="editor" style="color: rgba(0, 0, 0, 0.6)" class="pr-2">
              {{ editor.storage.characterCount.characters() }}/{{ configuration.maxCharacterCount }} {{ $t('chars') }}
            </span>
            <v-checkbox
                dense
                style="margin-top: 0px; padding-top: 0px!important;"
                v-model="advanceMode"
                :label="$t('advanceMode')"
                hide-details
            ></v-checkbox>
            <a v-if="advanceMode" class="pl-3" style="text-decoration: none"
               href="https://www.markdownguide.org/basic-syntax/" target="_blank">{{ $t('aboutMarkdown') }}</a>

          </v-col>
          <v-col class="pa-0 pr-3 d-flex"
                 style="align-items: center; justify-content: flex-end;"
                 md="3"
                 sm="3"
                 xs="12"
          >
            <AttachmentList v-if="attachmentSupport" :attachments="attachments" />
          </v-col>
        </v-row>
      </v-card-actions>
    </v-card>
  </section>
</template>

<script>
import TecnaMarkdown from "./TecnaMardkown"
import MarkdownToolbar from "./Toolbar/MarkdownToolbar"
import TextEditor from "./TextEditor"
import AttachmentList from "./AttachmentList";

const events = ['dragenter', 'dragover', 'dragleave', 'drop']
import MenuBar from "./Toolbar/MenuBar";
import {createEditor} from "./configuration/tiptapConfiguration"
import {parseSuggestions} from "./extensions/markdownSuggestionParser";

const toBase64 = file => new Promise((resolve, reject) => {
  const reader = new FileReader();
  reader.readAsDataURL(file);
  reader.onload = () => resolve(reader.result);
  reader.onerror = error => reject(error);
});

export default {
  name: "TcnTextEditor",
  props: {
    value: [Object, String],
    attachments: {
      type: Array,
      default: []
    },
    attachmentSupport: {
      type: Boolean,
      default: true
    },
    configuration: {
      type: Object,
      default: null
    }
  },
  components: {AttachmentList, MenuBar, TecnaMarkdown, MarkdownToolbar, TextEditor},
  computed: {
    model: {
      get() {
        return this.value
      },
      set(val) {
        this.$emit("input", val)
      },
    },
  },
  data() {
    return {
      advanceMode: false,
      dropZoneActive: false,
      editor: null,
    }
  },
  watch: {
    value(newVal) {
      this.setContent(newVal)
    },
    advanceMode(newVal) {
      if (!newVal) {
        this.editor.commands
            .setContent(this.editor.parseMarkdown(this.value))
        this.editor.commands
            .setContent(parseSuggestions(this.value, this.configuration))
      }
    }
  },
  mounted() {
    events.forEach((eventName) => {
      document.body.addEventListener(eventName, e => e.preventDefault())
    })
    this.editor = createEditor(this.model, this.configuration)

    this.editor.on('update', ({editor}) => {
      this.$emit("input", editor.getHTML())
    })
  },
  methods: {
    async onDrop(e) {
      if (e.dataTransfer.files[0].type.includes("image")) {
        const reader = new FileReader();
        reader.readAsDataURL(e.dataTransfer.files[0]);
        try {
          const result = await toBase64(e.dataTransfer.files[0]);
          this.editor.commands.setImage({src: result})
          this.setInactive()
          return;
        } catch (error) {
          console.error(error);
          return;
        }
      }
      this.setInactive()
    },

    setActive() {
      this.dropZoneActive = true
    },

    setInactive() {
      this.dropZoneActive = false
    },

    setContent(newVal) {
      if (this.editor && newVal === '') {
        this.editor.commands.clearContent()
      }
    },
  }
}
</script>

<style>
.test-markdown {
  display: flex;
  flex-direction: column;
}

.drag-area {
  height: 30vh;
  border-radius: 5px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  border: 2px dashed black;
}

.drop-zone-enable {
  display: none;
}

.tcn-editor {
  width: 100%;
  min-height: 150px;
  padding: 10px;
  border-top: 2px solid #00000021;
  border-bottom: 2px solid #00000021;
}

.insert-img {
  width: 80%
}

.suggestions {
  display: inline-flex;
  align-items: center;
}
</style>

<style lang="scss">
.ProseMirror {
  p {
    color: black;
  }

  table {
    border-collapse: collapse;
    table-layout: fixed;
    width: 100%;
    margin: 0;
    overflow: hidden;

    td,
    th {
      min-width: 1em;
      border: 2px solid #ced4da;
      padding: 3px 5px;
      vertical-align: top;
      box-sizing: border-box;
      position: relative;

      > * {
        margin-bottom: 0;
      }
    }

    th {
      font-weight: bold;
      text-align: left;
      background-color: #f1f3f5;
    }

    .selectedCell:after {
      z-index: 2;
      position: absolute;
      content: "";
      left: 0;
      right: 0;
      top: 0;
      bottom: 0;
      background: rgba(200, 200, 255, 0.4);
      pointer-events: none;
    }

    .column-resize-handle {
      position: absolute;
      right: -2px;
      top: 0;
      bottom: -2px;
      width: 4px;
      background-color: #adf;
      pointer-events: none;
    }

    p {
      margin: 0;
    }
  }

  > * + * {
    margin-top: 0.75em;
  }

  blockquote {
    padding-left: 1rem;
    border-left: 3px solid rgba(#0D0D0D, 0.1);
  }
}

.tableWrapper {
  padding: 1rem 0;
  overflow-x: auto;
}

.resize-cursor {
  cursor: ew-resize;
  cursor: col-resize;
}

ul[data-type="taskList"] {
  list-style: none;
  padding: 0;

  p {
    margin: 0;
  }

  li {
    display: flex;

    > label {
      flex: 0 0 auto;
      margin-right: 0.5rem;
      user-select: none;
    }

    > div {
      flex: 1 1 auto;
    }
  }
}
</style>


<i18n lang="i18n">
{
  "en": {
    "advanceMode": "Advance mode",
    "aboutMarkdown": "About Markdown",
    "chars": "chars",
    "dragAttachmentHere": "Drag the attachment here"
  },
  "pl": {
    "advanceMode": "Tryb zaawansowany",
    "aboutMarkdown": "Więcej o markdown",
    "chars": "znaków",
    "dragAttachmentHere": "Przeciągnij załącznik tutaj"
  }
}
</i18n>
